import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useI18n } from "../lib/i18n.jsx";
import { api } from "../lib/api.js";
import { Building2, Layers, Tag, MapPin, Users2, Bell, Mail, KeyRound, Sliders, FileText, Ban, Star, Zap, Ship, ArrowRight, Pause, Play, AlertTriangle } from "lucide-react";
import DailyDigestPanel from "../components/admin/DailyDigestPanel.jsx";
import NotificationsSettingsPanel from "../components/admin/NotificationsSettingsPanel.jsx";
import PermissionsMatrixPanel from "../components/admin/PermissionsMatrixPanel.jsx";
import SystemSettingsGlobalPanel from "../components/admin/SystemSettingsGlobalPanel.jsx";
import SurveyTemplatesPanel from "../components/admin/SurveyTemplatesPanel.jsx";
import NpsBenchmarkPanel from "../components/admin/NpsBenchmarkPanel.jsx";
import { UsersTab } from "../components/admin/UsersTab.jsx";
import { LookupTab } from "../components/admin/LookupTab.jsx";
import { OverridesTab } from "../components/admin/OverridesTab.jsx";
import { AuditTab } from "../components/admin/AuditTab.jsx";
import { DwellThresholdsTab } from "../components/admin/DwellThresholdsTab.jsx";
import { SlaSettingsTab } from "../components/admin/SlaSettingsTab.jsx";
import VehicleCataloguePanel from "../components/admin/VehicleCataloguePanel.jsx";
import DemoSeedPanel from "../components/admin/DemoSeedPanel.jsx";
import { Database } from "lucide-react";

const ADMIN_TABS = [
  { k: "users",          label: "Utilizadores",           icon: Users2 },
  { k: "brands",         label: "Marcas e Dealers",       icon: Building2 },
  { k: "catalogue",      label: "Catálogo de Viaturas",   icon: Layers },
  { k: "notifications",  label: "Notificações e Alertas", icon: Bell },
  { k: "digest",         label: "Digest diário",          icon: Mail },
  { k: "permissions",    label: "Permissões e Acessos",   icon: KeyRound },
  { k: "system",         label: "Definições do sistema",  icon: Sliders },
  { k: "email_switch",   label: "Pausar / enviar emails", icon: Mail },
  { k: "logistics",      label: "Logística de Viaturas",  icon: Ship },
  { k: "broadcast",      label: "Email a toda a equipa",  icon: Mail },
  { k: "survey_tpl",     label: "Templates de inquérito", icon: Star },
  { k: "nps_bench",      label: "Alertas NPS",            icon: Zap },
  { k: "audit",          label: "Registo de auditoria",   icon: FileText },
  { k: "overrides",      label: "Log de overrides",       icon: Ban },
  { k: "demo_seed",      label: "Dataset de demo",        icon: Database },
];

export default function AdminPage() {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") && ADMIN_TABS.some((x) => x.k === searchParams.get("tab"))
    ? searchParams.get("tab")
    : "users";
  const [tab, setTab] = useState(initialTab);

  const switchTab = (k) => {
    setTab(k);
    setSearchParams({ tab: k }, { replace: true });
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
      <h1 className="font-heading text-2xl font-bold text-[#1A1A2E] mb-1">{t("admin_settings")}</h1>
      <p className="text-xs text-[#6C757D] mb-4">Hub de configuração central do Ronil Command Center.</p>
      <div className="flex flex-col md:flex-row gap-4">
        <aside className="md:w-56 shrink-0" data-testid="admin-subnav">
          <nav className="bg-white border border-gray-200 rounded-sm p-1 md:sticky md:top-4">
            {ADMIN_TABS.map(({ k, label, icon: Icon, badge }) => (
              <button
                key={k}
                onClick={() => switchTab(k)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-sm text-left transition-colors ${
                  tab === k
                    ? "bg-[#1F3864] text-white"
                    : "text-[#495057] hover:bg-[#F4F6F8]"
                }`}
                data-testid={`admin-tab-${k}`}
              >
                <span className="flex items-center gap-2">
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </span>
                {badge && (
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-semibold ${
                    tab === k ? "bg-white/20 text-white" : "bg-[#FFF7E6] text-[#856404]"
                  }`}>{badge}</span>
                )}
              </button>
            ))}
          </nav>
        </aside>

        <div className="flex-1 min-w-0">
          {tab === "users" && <UsersTab />}
          {tab === "brands" && <BrandsAndBranchesTab />}
          {tab === "notifications" && <NotificationsSettingsPanel />}
          {tab === "digest" && <DailyDigestPanel />}
          {tab === "permissions" && <PermissionsMatrixPanel />}
          {tab === "system" && <SystemSettingsTab />}
          {tab === "email_switch" && <EmailSwitchTab />}
          {tab === "logistics" && <LogisticsAdminTab />}
          {tab === "broadcast" && <BroadcastAdminTab />}
          {tab === "survey_tpl" && <SurveyTemplatesPanel />}
          {tab === "nps_bench" && <NpsBenchmarkPanel />}
          {tab === "catalogue" && <VehicleCataloguePanel />}
          {tab === "audit" && <AuditTab />}
          {tab === "overrides" && <OverridesTab />}
          {tab === "demo_seed" && <DemoSeedPanel />}
        </div>
      </div>
    </div>
  );
}

function BrandsAndBranchesTab() {
  return (
    <div className="space-y-6">
      <section>
        <h2 className="font-heading text-lg font-bold text-[#1A1A2E] mb-2 flex items-center gap-2">
          <Tag className="w-4 h-4 text-[#1F3864]" /> Marcas
        </h2>
        <LookupTab kind="brand" />
      </section>
      <section>
        <h2 className="font-heading text-lg font-bold text-[#1A1A2E] mb-2 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-[#1F3864]" /> Filiais (Dealers)
        </h2>
        <LookupTab kind="branch" />
      </section>
    </div>
  );
}

function SystemSettingsTab() {
  return (
    <div className="space-y-6">
      <section>
        <h2 className="font-heading text-lg font-bold text-[#1A1A2E] mb-2">Limites de bloqueio por estado</h2>
        <p className="text-xs text-[#6C757D] mb-3">SLAs por estado e por entidade. Alertas automáticos disparam quando ultrapassados.</p>
        <DwellThresholdsTab />
      </section>
      <section>
        <h2 className="font-heading text-lg font-bold text-[#1A1A2E] mb-2">SLA por marca</h2>
        <p className="text-xs text-[#6C757D] mb-3">Cada marca pode ter tempos de resposta específicos (garantia, cotação, etc.).</p>
        <SlaSettingsTab />
      </section>
      <section className="bg-[#F4F6F8] border border-gray-200 rounded-sm p-4">
        <h2 className="font-heading text-sm font-bold text-[#1A1A2E] mb-1">Definições globais</h2>
        <p className="text-xs text-[#6C757D] mb-3">Retenção de notificações, intervalo do scan de bloqueios, limite de linhas Excel e fuso horário — guardados em <code>system_settings.global_settings</code> e lidos por outros módulos via <code>get_global_setting_sync</code>.</p>
        <SystemSettingsGlobalPanel />
      </section>
    </div>
  );
}

function EmailSwitchTab() {
  const [state, setState] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const s = await api.get("/admin/email-switch");
      setState(s.data);
      setReason(s.data.reason || "");
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    if (!state) return;
    const nextPaused = !state.paused;
    if (nextPaused && !window.confirm("Pausar TODOS os emails automáticos (digest, alertas logística, broadcasts)? Pára envios imediatamente.")) return;
    setBusy(true);
    try {
      await api.post("/admin/email-switch", { paused: nextPaused, reason });
      await load();
    } catch (e) { alert("Falha: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  if (!state) return <div className="text-sm text-slate-400">A carregar…</div>;

  return (
    <div className="space-y-4" data-testid="email-switch-tab">
      <div className="text-xs text-slate-500">
        Este pod: {state.is_preview
          ? <span className="inline-flex items-center gap-1 text-[10px] font-heading font-bold uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 rounded">Preview — emails bloqueados por omissão</span>
          : <span className="inline-flex items-center gap-1 text-[10px] font-heading font-bold uppercase tracking-widest bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded">● Produção</span>}
      </div>

      <section className={`border rounded-md p-5 ${state.paused ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200"}`}>
        <div className="flex items-start gap-4">
          <div className={`rounded-full p-2.5 ${state.paused ? "bg-rose-200 text-rose-800" : "bg-emerald-200 text-emerald-800"}`}>
            {state.paused ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-lg font-bold text-[#1A1A2E]">
              {state.paused ? "Emails PAUSADOS" : "Emails a sair normalmente em produção"}
            </h2>
            <p className="text-xs text-slate-600 mt-1">
              {state.paused
                ? "Nenhum email automático está a sair — digest, alertas de logística, broadcasts manuais, tudo pausado."
                : "Em produção, os schedulers disparam normalmente. Em preview, os envios estão sempre bloqueados por omissão (ligados caso-a-caso pelo administrador)."}
            </p>
            {state.updated_at && (
              <p className="text-[11px] text-slate-500 mt-2">
                Última alteração: {state.updated_at.slice(0, 16).replace("T", " ")} por {state.updated_by_name || "—"}
                {state.reason && <> · motivo: <em>{state.reason}</em></>}
              </p>
            )}
          </div>
        </div>

        <div className="mt-4 flex items-end gap-2 flex-wrap">
          <label className="flex-1 min-w-[240px]">
            <span className="text-[10px] uppercase tracking-widest font-heading font-semibold text-slate-500 block mb-1">Motivo (opcional)</span>
            <input className="w-full border border-slate-300 rounded-md px-2.5 py-1.5 text-sm" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex.: Manutenção" data-testid="switch-reason" />
          </label>
          <button onClick={toggle} disabled={busy} data-testid="switch-toggle"
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-heading font-semibold text-white ${state.paused ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"} disabled:opacity-50`}>
            {state.paused ? <><Play className="w-4 h-4" /> Retomar envios</> : <><Pause className="w-4 h-4" /> Pausar envios</>}
          </button>
        </div>
      </section>

      <section className="bg-white border border-gray-200 rounded-md p-4">
        <h3 className="font-heading text-sm font-bold text-[#1A1A2E] mb-1 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> O que esta funcionalidade cobre
        </h3>
        <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside">
          <li>Digest diário/semanal (SOMs atrasadas, PECs, tarefas, SWCs, Logística)</li>
          <li>Alertas automáticos da Logística (ETA, W7, docs em falta…)</li>
          <li>Benchmark mensal NPS</li>
          <li>Broadcasts manuais (Admin → Email a toda a equipa)</li>
          <li>Qualquer email que use <code>email_sender.send_email()</code></li>
        </ul>
        <p className="text-[11px] text-slate-500 mt-2"><strong>Não cobre</strong>: notificações in-app (bell 🔔 continua a funcionar).</p>
      </section>
    </div>
  );
}

function BroadcastAdminTab() {  return (
    <section className="bg-white border border-gray-200 rounded-md p-5">
      <h2 className="font-heading text-lg font-bold text-[#1A1A2E] mb-1 flex items-center gap-2">
        <Mail className="w-4 h-4 text-[#1F3864]" /> Comunicado por email a toda a equipa
      </h2>
      <p className="text-xs text-[#6C757D] mb-4">
        Compõe um email institucional em Markdown, pré-visualiza, faz um envio de teste
        para o teu próprio endereço e depois envia para toda a equipa (ou um subconjunto).
        Guarda histórico de todos os envios anteriores.
      </p>
      <Link
        to="/admin/broadcast"
        className="inline-flex items-center gap-2 bg-[#1F3864] hover:bg-[#1A2F56] text-white px-4 py-2 rounded-md text-sm font-heading font-semibold"
        data-testid="admin-broadcast-link"
      >
        Compor novo comunicado <ArrowRight className="w-4 h-4" />
      </Link>
    </section>
  );
}

function LogisticsAdminTab() {
  return (
    <div className="space-y-4">
      <section className="bg-white border border-gray-200 rounded-md p-5">
        <h2 className="font-heading text-lg font-bold text-[#1A1A2E] mb-1 flex items-center gap-2">
          <Ship className="w-4 h-4 text-[#1F3864]" /> Configurações de Logística
        </h2>
        <p className="text-xs text-[#6C757D] mb-4">
          Define os limites de alertas W7 (aviso e crítico), tolerância ETA, documentos obrigatórios,
          estados que exigem inspecção e quem recebe os alertas automáticos (responsável do VIN,
          gestor do dealer, HQ Maputo).
        </p>
        <Link
          to="/logistica/configuracoes"
          className="inline-flex items-center gap-2 bg-[#1F3864] hover:bg-[#1A2F56] text-white px-4 py-2 rounded-md text-sm font-heading font-semibold"
          data-testid="admin-logistics-settings-link"
        >
          Abrir Configurações da Logística <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
      <section className="bg-white border border-gray-200 rounded-md p-5">
        <h2 className="font-heading text-base font-bold text-[#1A1A2E] mb-1">Painel de Alertas</h2>
        <p className="text-xs text-[#6C757D] mb-3">
          Vê todos os alertas enviados nas últimas 24h (ETA ultrapassado, W7 fora de prazo,
          documentos em falta, inspecções pendentes) e corre o scanner manualmente.
        </p>
        <Link
          to="/logistica/alertas"
          className="inline-flex items-center gap-2 bg-white border border-[#1F3864] text-[#1F3864] hover:bg-[#1F3864]/5 px-4 py-2 rounded-md text-sm font-heading font-semibold"
          data-testid="admin-logistics-alerts-link"
        >
          Abrir Painel de Alertas <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </div>
  );
}
